import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, ArrowDownRight, Play } from "lucide-react";
import { btnPrimary, btnOutline } from "../components/portfolio/styles";
import Seo from "../components/portfolio/Seo";
import {
  Reveal,
  StockMateInvoice,
  StockMateLedger,
  StorepilotDash,
  AiChat,
  BrowserSite,
} from "../components/portfolio/fragments";

const STOCKMATE_LIVE = "https://play.google.com/store/apps/details?id=com.stockmate.app";
const STOREPILOT_LIVE = "https://play.google.com/store/apps/details?id=com.dixigen.storepilot";
const LINKEDIN = "https://www.linkedin.com/in/mehedihassain";
const CONTACT_URL =
  "https://mail.google.com/mail/?view=cm&fs=1&to=saidtomehedi@gmail.com";

const floatA = { animation: "float-soft 7s ease-in-out infinite alternate" };
const floatB = { animation: "float-soft 8.5s ease-in-out infinite alternate-reverse" };
const floatC = { animation: "float-soft 6s ease-in-out infinite alternate" };

const EASE = [0.22, 1, 0.36, 1];

const heroLines = [
  { plain: "I make digital products clearer," },
  { plain: "more useful and ", em: "more coherent." },
];

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const x1 = useTransform(sx, (v) => v * 1.1);
  const y1 = useTransform(sy, (v) => v * 1.1);
  const x2 = useTransform(sx, (v) => v * -0.7);
  const y2 = useTransform(sy, (v) => v * -0.7);
  const x3 = useTransform(sx, (v) => v * 0.5);
  const y3 = useTransform(sy, (v) => v * -0.9);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
      className="relative hidden sm:block"
      aria-hidden="true"
      data-testid="hero-visual"
    >
      <div className="absolute inset-x-16 top-1/2 -translate-y-1/2 h-72 bg-[radial-gradient(ellipse_at_center,hsl(262_83%_58%/0.14),transparent_65%)]" />
      <div className="relative flex items-center justify-center gap-6 lg:gap-10 py-4">
        <motion.div style={{ x: x1, y: y1 }} className="-rotate-6 -mt-8">
          <div style={floatA}>
            <StockMateInvoice />
          </div>
        </motion.div>
        <motion.div style={{ x: x2, y: y2 }} className="rotate-2 mt-10 z-10">
          <div style={floatB}>
            <StorepilotDash />
          </div>
        </motion.div>
        <motion.div style={{ x: x3, y: y3 }} className="rotate-6 -mt-4">
          <div style={floatC}>
            <AiChat />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const AiMorph = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 85%", "end 45%"] });
  const chatOpacity = useTransform(scrollYProgress, [0.3, 0.7], [1, 0]);
  const chatY = useTransform(scrollYProgress, [0.3, 0.7], [0, -24]);
  const siteOpacity = useTransform(scrollYProgress, [0.3, 0.7], [0, 1]);
  const siteY = useTransform(scrollYProgress, [0.3, 0.7], [24, 0]);
  return (
    <div ref={ref} className="relative h-[320px]" data-testid="ai-morph-visual">
      <motion.div style={{ opacity: chatOpacity, y: chatY }} className="absolute inset-0 flex items-center justify-center">
        <AiChat />
      </motion.div>
      <motion.div style={{ opacity: siteOpacity, y: siteY }} className="absolute inset-0 flex items-center justify-center">
        <BrowserSite />
      </motion.div>
    </div>
  );
};

const AutoMorph = ({ first, second, testId }) => {
  const [showSecond, setShowSecond] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setShowSecond((v) => !v), 3400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative h-[300px]" data-testid={testId}>
      <motion.div animate={{ opacity: showSecond ? 0 : 1 }} transition={{ duration: 0.9, ease: EASE }} className="absolute inset-0 flex items-center justify-center">
        {first}
      </motion.div>
      <motion.div animate={{ opacity: showSecond ? 1 : 0 }} transition={{ duration: 0.9, ease: EASE }} className="absolute inset-0 flex items-center justify-center">
        {second}
      </motion.div>
    </div>
  );
};

const marqueeWords = ["Product Thinking", "UX/UI", "Figma", "AI for Product Design", "Launch", "Real Users", "Feedback", "Iteration"];

const Marquee = () => (
  <div className="overflow-hidden py-10 border-y border-border" aria-hidden="true" data-testid="editorial-marquee">
    <div className="marquee-track flex whitespace-nowrap w-max">
      {[0, 1].map((half) => (
        <span key={half} className="flex items-center">
          {marqueeWords.map((w) => (
            <span key={`${half}-${w}`} className="flex items-center">
              <span className="font-display italic text-3xl md:text-5xl text-foreground/15 px-6">{w}</span>
              <span className="w-2 h-2 rounded-full bg-primary/40 shrink-0" />
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

const Kicker = ({ children }) => (
  <div className="flex items-center gap-4">
    <p className="text-sm font-semibold tracking-[0.25em] text-primary shrink-0">{children}</p>
    <span className="flex-1 border-t border-border" aria-hidden="true" />
  </div>
);

const Evidence = ({ stats, source }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-3 gap-3">
      {stats.map(([v, k]) => (
        <div key={k} className="rounded-xl border border-border p-4">
          <p className="text-xl md:text-2xl font-bold text-primary">{v}</p>
          <p className="text-xs text-muted-foreground mt-1">{k}</p>
        </div>
      ))}
    </div>
    <p className="text-[11px] text-muted-foreground/70 italic leading-relaxed">{source}</p>
  </div>
);

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((c) => (
      <span
        key={c}
        className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
      >
        {c}
      </span>
    ))}
  </div>
);

export default function Home() {
  return (
    <div className="max-w-4xl mx-auto" data-testid="home-page">
      <Seo
        title="Mehedi Hassain | Product Designer — Mobile & Web Products"
        description="Mehedi Hassain is a Product Designer from Kolkata, India who designs and ships real mobile and web products — StockMate and Storepilot, live on Google Play."
      />
      {/* 01 — HERO */}
      <section className="pt-10 md:pt-16 pb-10" data-testid="hero-section">
        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4"
          >
            <p className="text-xs md:text-sm font-semibold tracking-[0.18em] md:tracking-[0.25em] text-primary shrink-0" data-testid="hero-kicker">
              PRODUCT DESIGNER · KOLKATA, INDIA
            </p>
            <span className="flex-1 border-t border-border" aria-hidden="true" />
          </motion.div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[1.08] tracking-[-0.02em] text-foreground" data-testid="hero-headline">
            {heroLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line.plain}
                  {line.em && <em className="italic text-primary">{line.em}</em>}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
          >
            <p className="text-lg md:text-xl font-normal text-muted-foreground leading-relaxed tracking-[-0.01em] max-w-lg">
              I design and build digital products — from the first idea to a working app, crafted
              screen by screen in Figma.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <a href="#selected-work" className={btnPrimary} data-testid="hero-explore-btn">
                Explore Work
                <ArrowDownRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform" />
              </a>
              <Link to="/about" className={btnOutline} data-testid="hero-about-btn">
                About Me
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground"
            data-testid="hero-status"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            Open to Product Designer opportunities
          </motion.p>
        </div>
        <div className="mt-12">
          <HeroVisual />
          <div className="sm:hidden flex justify-center py-4" aria-hidden="true">
            <StockMateInvoice />
          </div>
        </div>
      </section>

      <Marquee />

      {/* 02 — CONTEXT */}
      <section className="py-20 md:py-28 space-y-8" data-testid="context-section">
        <Reveal>
          <Kicker>02 · CONTEXT</Kicker>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="font-display text-2xl md:text-4xl leading-snug tracking-[-0.01em] text-foreground max-w-3xl">
            No-code apps in 2023. Freelance projects. Figma. React Native.{" "}
            <em className="italic text-primary">Now — products of my own,</em> designed in Figma
            and built with AI tools.
          </p>
        </Reveal>
      </section>

      {/* 03 — SELECTED WORK */}
      <section id="selected-work" className="py-20 md:py-28 border-t border-border space-y-24 scroll-mt-8" data-testid="selected-work">
        <Reveal>
          <Kicker>03 · SELECTED WORK</Kicker>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] text-foreground mt-6">
            Real products. <em className="italic text-primary">Continuous learning.</em>
          </h2>
        </Reveal>

        {/* 01 — STOCKMATE */}
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-testid="stockmate-feature">
          <Reveal className="relative h-[380px] md:h-[440px] order-2 lg:order-1" aria-hidden="true">
            <div className="absolute left-2 top-4 -rotate-2" style={floatA}>
              <StockMateInvoice />
            </div>
            <div className="absolute right-0 bottom-4 rotate-2" style={floatB}>
              <StockMateLedger />
            </div>
          </Reveal>
          <div className="space-y-6 order-1 lg:order-2">
            <Reveal>
              <span className="font-display text-xl text-primary/40">01</span>
              <h3 className="font-display text-4xl md:text-5xl text-foreground mt-1">StockMate</h3>
              <p className="font-display italic text-xl md:text-2xl text-muted-foreground mt-2">
                Designing for the everyday realities of retail.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-muted-foreground leading-relaxed">
                An offline-first inventory and billing app for small retailers — designed in Figma,
                shipped on Google Play, refined with real users.
              </p>
              <p className="text-sm text-muted-foreground mt-3">
                <span className="font-semibold text-foreground">Role:</span> Product Designer ·
                AI-Assisted Developer
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Evidence
                stats={[["2.08K", "Total Installs"], ["747", "Avg. MAU"], ["98.7", "Avg. DAU"]]}
                source="Google Play Console, approx. Sep 25 – Oct 22, 2026. Period-specific, not lifetime averages."
              />
            </Reveal>
            <Reveal delay={0.12}>
              <Chips items={["Mobile App", "Retail Tech", "Business", "Offline-First"]} />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={STOCKMATE_LIVE} target="_blank" rel="noopener noreferrer" className={btnPrimary} data-testid="stockmate-live-btn">
                  <Play className="h-4 w-4" />
                  View Live
                </a>
                <Link to="/projects/stockmate" className={btnOutline} data-testid="stockmate-case-study-btn">
                  Case Study
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>

        {/* 02 — STOREPILOT */}
        <div className="grid lg:grid-cols-2 gap-12 items-center border-t border-border pt-24" data-testid="storepilot-feature">
          <div className="space-y-6">
            <Reveal>
              <span className="font-display text-xl text-primary/40">02</span>
              <h3 className="font-display text-4xl md:text-5xl text-foreground mt-1">Storepilot</h3>
              <p className="font-display italic text-xl md:text-2xl text-muted-foreground mt-2">
                Taking the shop-management experience further.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-muted-foreground leading-relaxed">
                StockMate's evolution — inventory, billing, payments and cloud sync in one
                consistent shop-management experience.
              </p>
              <p className="text-sm text-muted-foreground mt-3">
                <span className="font-semibold text-foreground">Role:</span> Product Designer ·
                AI-Assisted Developer
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Evidence
                stats={[["87", "Total Installs"], ["15.1", "Avg. MAU"], ["2", "Avg. DAU"]]}
                source="Google Play Console, approx. Sep 25 – Oct 22, 2026."
              />
            </Reveal>
            <Reveal delay={0.12}>
              <Chips items={["Mobile App", "Retail Tech", "Business", "SaaS"]} />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={STOREPILOT_LIVE} target="_blank" rel="noopener noreferrer" className={btnPrimary} data-testid="storepilot-live-btn">
                  <Play className="h-4 w-4" />
                  View Live
                </a>
                <Link to="/projects/storepilot" className={btnOutline} data-testid="storepilot-case-study-btn">
                  Case Study
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal className="relative h-[380px] md:h-[440px]" aria-hidden="true">
            <div className="absolute right-2 top-6 rotate-2" style={floatB}>
              <StorepilotDash />
            </div>
            <div className="absolute left-0 bottom-6 -rotate-2" style={floatC}>
              <StockMateLedger />
            </div>
          </Reveal>
        </div>

        {/* 03 — BROOKLY AI */}
        <div className="grid lg:grid-cols-2 gap-12 items-center border-t border-border pt-24" data-testid="ai-builder-feature">
          <Reveal className="relative order-2 lg:order-1" aria-hidden="true">
            <div className="hidden md:block">
              <AiMorph />
            </div>
            <div className="md:hidden">
              <AutoMorph first={<AiChat />} second={<BrowserSite />} testId="ai-morph-mobile" />
            </div>
          </Reveal>
          <div className="space-y-6 order-1 lg:order-2">
            <Reveal>
              <span className="font-display text-xl text-primary/40">03</span>
              <h3 className="font-display text-4xl md:text-5xl text-foreground mt-1">Brookly AI</h3>
              <p className="font-display italic text-xl md:text-2xl text-muted-foreground mt-2">
                A website that starts from a conversation.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-muted-foreground leading-relaxed">
                An AI website builder concept — describe your business, get a tailored website,
                customise and publish.
              </p>
              <p className="text-sm text-muted-foreground mt-3">
                <span className="font-semibold text-foreground">Role:</span> Product Designer
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Chips items={["AI Product", "Web Design", "Mobile App", "SaaS"]} />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/projects/ai-website-builder" className={btnOutline} data-testid="ai-builder-case-study-btn">
                  Case Study
                  <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>
              <p className="text-xs text-muted-foreground italic mt-3">Concept exploration.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 04 — WHAT CONNECTS MY WORK */}
      <section className="py-20 md:py-28 border-t border-border space-y-12" data-testid="pillars-section">
        <Reveal>
          <Kicker>04 · WHAT CONNECTS MY WORK</Kicker>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-[-0.02em] text-foreground max-w-3xl mt-6">
            The screen is only <em className="italic text-primary">part of the product.</em>
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          {[
            ["PRODUCT", "What problem are we solving?"],
            ["EXPERIENCE", "How should it feel?"],
            ["TECHNOLOGY", "How can it actually work?"],
            ["REFINEMENT", "What can be better?"],
          ].map(([k, q], i) => (
            <Reveal key={k} delay={i * 0.06}>
              <div className="bg-background p-6 md:p-8 h-full space-y-4 hover:bg-primary/5 transition-colors duration-300" data-testid={`pillar-${k.toLowerCase()}`}>
                <p className="text-xs font-bold tracking-[0.2em] text-primary">{k}</p>
                <p className="font-display text-xl md:text-2xl text-foreground leading-snug">{q}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 05 — HOW I WORK */}
      <section className="py-20 md:py-28 border-t border-border space-y-12" data-testid="process-section">
        <Reveal>
          <Kicker>05 · HOW I WORK</Kicker>
        </Reveal>
        <div>
          {[
            ["01", "Understand", "Problem, user, context, constraints."],
            ["02", "Define", "What the product must do — and what matters most."],
            ["03", "Shape", "Screens, hierarchy, interactions, journey."],
            ["04", "Build", "AI-assisted development — reviewed and improved, never accepted blindly."],
            ["05", "Refine", "Layout, typography and consistency in Figma."],
            ["06", "Test", "Use it, gather feedback, find the real problems."],
            ["07", "Iterate", "Improve — and keep improving."],
          ].map(([num, title, text], i) => (
            <Reveal key={num} delay={i * 0.04}>
              <div
                className="group flex flex-col md:flex-row md:items-center gap-1 md:gap-10 py-5 border-t border-border last:border-b hover:bg-primary/5 transition-colors duration-300 px-2 md:px-4 -mx-2 md:-mx-4 rounded-lg"
                data-testid={`process-${num}`}
              >
                <span className="font-display text-2xl text-primary/30 group-hover:text-primary transition-colors w-14 shrink-0">
                  {num}
                </span>
                <h3 className="text-xl font-semibold text-foreground md:w-40 shrink-0">{title}</h3>
                <p className="text-muted-foreground">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="font-display italic text-2xl md:text-4xl text-foreground leading-snug border-l-4 border-primary pl-6 max-w-3xl">
            A working first version is a starting point, not the finish line.
          </p>
        </Reveal>
      </section>

      {/* 06 — ABOUT INTRO */}
      <section className="py-20 md:py-28 border-t border-border space-y-8" data-testid="about-intro-section">
        <Reveal>
          <Kicker>06 · ABOUT</Kicker>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="font-display text-2xl md:text-4xl leading-snug tracking-[-0.01em] text-foreground max-w-3xl">
            I'm Mehedi — I learned design{" "}
            <em className="italic text-primary">by building real products.</em> I care about what
            to build, how it should work and whether it holds up in the real world.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/about" className="group inline-flex items-center gap-2 text-sm font-semibold text-primary border-b border-primary/40 pb-1 hover:border-primary transition-colors" data-testid="more-about-link">
            About Me
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
      </section>

      {/* 07 — LET'S TALK */}
      <section className="py-24 md:py-32 border-t border-border text-center space-y-8" data-testid="final-cta-section">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-[-0.02em] text-foreground">
            Have a product <em className="italic text-primary">worth making better?</em>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-muted-foreground max-w-xl mx-auto">
            I care about thoughtful problems, useful products and the details that make experiences
            work better. I'm looking to join a team where I can contribute to product thinking,
            design and execution.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" className={btnPrimary} data-testid="cta-contact-btn">
              Get in touch
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a href="/Mehedi-Hassain-Resume.pdf" target="_blank" rel="noopener noreferrer" className={btnOutline} data-testid="cta-resume-btn">
              View Resume
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" className={btnOutline} data-testid="cta-linkedin-btn">
              LinkedIn
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
